/*
    Keeps beforeNotify's "false" meaning "do not send" on pages where something has replaced
    the global Error constructor.

    Honeybadger's client decides whether a notice was vetoed with
    `preConditionsResult instanceof Error` (@honeybadger-io/core, still so in 6.11.1). Code
    injected into a page - an extension, an in-app browser - can swap window.Error for a
    wrapper; ours show up in stacks as `new_constructor (<anonymous>)`. The veto Honeybadger
    builds with `new Error(...)` is then a native Error, the check asks about the wrapper, it
    answers no, and notify() sends the notice our filter just rejected.

    That is why the ignore list had stopped working exactly where it matters: #418, #423 and
    "Converting circular structure to JSON" kept arriving for months from the pages running
    injected code, while the same filters held everywhere else. Proven against the real
    client: a vetoed notice is sent 0 times with a native Error and once with a wrapped one.

    The fix re-homes the veto onto whatever Error the page has right now, so the instanceof
    check sees it for what it is. It touches Honeybadger's private __runPreconditions, which
    is the trade: honeybadgerFilters.test.js runs a real client with a replaced Error and
    fails if an upgrade moves this from under us.
*/
const isThenable = (value) => !!value && typeof value.then === 'function';

const asCurrentError = (result) => {
    if (!result || typeof result !== 'object' || isThenable(result)) return result;
    const CurrentError = globalThis.Error;
    if (typeof CurrentError !== 'function' || result instanceof CurrentError) return result;
    if (!CurrentError.prototype || typeof CurrentError.prototype !== 'object') return result;

    const rehomed = Object.create(CurrentError.prototype);
    rehomed.name = result.name;
    rehomed.message = result.message;
    rehomed.stack = result.stack;
    return rehomed;
};

export const guardPreconditions = (client) => {
    const original = client && client.__runPreconditions;
    if (typeof original !== 'function' || original.__guarded) return client;

    const guarded = function guardedRunPreconditions(notice) {
        const result = original.call(this, notice);
        return isThenable(result) ? result.then(asCurrentError) : asCurrentError(result);
    };
    guarded.__guarded = true;
    client.__runPreconditions = guarded;
    return client;
};
